<script lang="ts">
import type { VariantProps } from 'tailwind-variants';
import type { PropType, VNode } from 'vue';
import type { AnyItem, DisplayTemplate, DisplayTemplateRenderPlan, GlobalCollectionId, ResolvedDisplayTemplateRenderNode } from '#layers/data-model/public';
import type { ThemeSlots } from '#layers/ui/public';
import { tv } from 'tailwind-variants';
import { computed, defineComponent, h } from 'vue';
import RenderFieldDisplay from './RenderFieldDisplay.vue';

const renderDisplayTemplate = tv({
	slots: {
		root: 'flex min-w-0 items-center overflow-hidden leading-8',
		content: 'flex min-w-0 items-center overflow-hidden',
	},
	variants: {
		layout: {
			'fill': {
				root: '-mx-2.5 flex-1 px-2.5',
				content: '-mx-2.5 flex-1 px-2.5',
			},
			'fit-content': {
				root: 'w-max max-w-full flex-none',
				content: 'w-max max-w-full flex-none',
			},
		},
	},
	defaultVariants: {
		layout: 'fill',
	},
});

type RenderDisplayTemplateVariants = VariantProps<typeof renderDisplayTemplate>;

export interface RenderDisplayTemplateProps<T extends AnyItem> {
	item: T;
	displayTemplate?: DisplayTemplate | null;
	collectionId?: GlobalCollectionId;
	renderPlan?: DisplayTemplateRenderPlan | null;
	class?: any;
	/**
	 * Fill normal display cells by default, or size to content for inline affordances that reserve their own trailing space.
	 */
	layout?: RenderDisplayTemplateVariants['layout'];
	/**
	 * Override display template slot classes.
	 */
	ui?: ThemeSlots<typeof renderDisplayTemplate>;
	/**
	 * Set to true if this is the root node of a display template.
	 * This can change the behavior of rendered content, e.g., related items might not show inline links
	 */
	root?: boolean;
}

const RenderPriorityNodes = defineComponent({
	name: 'RenderDisplayTemplatePriorityNodes',
	props: {
		nodes: {
			type: Array as PropType<ResolvedDisplayTemplateRenderNode[]>,
			required: true,
		},
		root: {
			type: Boolean,
			required: true,
		},
		allowRootFieldBleed: {
			type: Boolean,
			required: true,
		},
	},
	setup(componentProps) {
		return () => renderPriorityNodes(componentProps.nodes, componentProps.root, componentProps.allowRootFieldBleed);
	},
});

function renderPriorityNodes(renderNodes: ResolvedDisplayTemplateRenderNode[], root: boolean, allowRootFieldBleed: boolean, renderIndex = 0): VNode[] {
	const renderableNode = renderNodes[renderIndex];
	if (!renderableNode) return [];

	const isLast = renderIndex === renderNodes.length - 1;
	const isRootField = allowRootFieldBleed && root && renderNodes.length === 1;
	const renderedNodes = [renderToken(renderableNode, isLast, isRootField)];

	if (!isLast) {
		renderedNodes.push(h('span', { class: 'flex min-w-0 flex-auto items-center overflow-hidden' }, renderPriorityNodes(renderNodes, root, allowRootFieldBleed, renderIndex + 1)));
	}

	return renderedNodes;
}

function renderToken(renderableNode: ResolvedDisplayTemplateRenderNode, isLast: boolean, isRootField: boolean): VNode {
	const nodeClass = isLast ? 'min-w-0 flex-auto' : 'max-w-full min-w-0 shrink-0';

	if ('text' in renderableNode) {
		return h('span', {
			'class': `${nodeClass} overflow-hidden whitespace-pre`,
			'data-testid': 'display-template-text-node',
		}, renderableNode.text);
	}

	// The host owns vertical centering: this wrapper is a flex item, so its child is
	// blockified and centered by the row's `items-center` no matter what the block
	// renders (inline badge, icon chip, thumbnail). A flex box can't `text-overflow`,
	// so we only clip here; blocks ellipsize their own text.
	const boxClass = 'flex items-center overflow-hidden rounded-md max-h-lh';
	const nodeClassWithBleed = isRootField ? `${nodeClass} -mx-2.5 ${boxClass} px-2.5` : `${nodeClass} ${boxClass}`;

	return h('span', { class: nodeClassWithBleed }, [
		h(RenderFieldDisplay, {
			fieldId: renderableNode.fieldId,
			value: renderableNode.value,
			item: renderableNode.leafItem,
			root: isRootField,
		}),
	]);
}
</script>

<script setup lang="ts" generic="T extends AnyItem =AnyItem">
import { useI18n } from 'vue-i18n';
import { isNullishOrEmpty } from '#layers/base/public';
import { createDisplayTemplateRenderPlan, isEmptyDisplayTemplate, resolveDisplayTemplateRenderPlan, useCollection } from '#layers/data-model/public';

const props = withDefaults(defineProps<RenderDisplayTemplateProps<T>>(), {
	layout: 'fill',
	root: true,
});

const { t } = useI18n();

const { collection } = useCollection(props.collectionId);
const renderableNodes = useRenderableTemplateNodes();

const hasTemplate = computed(() => props.renderPlan || !isEmptyDisplayTemplate(props.displayTemplate));
const configurationError = computed(() => !hasTemplate.value || isNullishOrEmpty(renderableNodes.value) ? t('display_template.no_display_template') : undefined);
const ui = computed(() => renderDisplayTemplate({ layout: props.layout }));

function useRenderableTemplateNodes() {
	const renderPlan = computed<DisplayTemplateRenderPlan | null>(() => {
		if (props.renderPlan) return props.renderPlan;
		if (isEmptyDisplayTemplate(props.displayTemplate)) return null;
		if (!collection.value) return null;
		return createDisplayTemplateRenderPlan(props.displayTemplate, collection.value);
	});

	return computed<ResolvedDisplayTemplateRenderNode[]>(() => {
		if (!renderPlan.value) return [];
		return resolveDisplayTemplateRenderPlan(renderPlan.value, props.item);
	});
}
</script>

<template>
	<div :class="ui.root({ class: [props.ui?.root, props.class] })">
		<DisplayTemplateConfigurationError v-if="configurationError" :error="configurationError" />
		<div v-else :class="ui.content({ class: props.ui?.content })">
			<RenderPriorityNodes :nodes="renderableNodes" :root="props.root" :allow-root-field-bleed="props.layout === 'fill'" />
		</div>
	</div>
</template>
